/**
 * ContractGeneratorDialog - Dialog pentru generarea contractelor de prestări servicii
 * Include formular cu câmpuri și preview în timp real + export PDF
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { safeOpenHTMLWindow } from '@/utils/safeHTML';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  FileText, Hash, Calendar, Users, Building, MapPin, 
  DollarSign, Clock, Shield, Save, Download, Eye,
  FormInput, Search, Loader2 
} from 'lucide-react';
import ContractPreview from './ContractPreview';
import { generateContractHTML, generateContractFilename, formatDate } from './contractUtils';
import { checkAndDeductCredits } from '@/utils/creditSystem';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialContractData = {
  // Număr și Date Contract
  contract_number: '',
  contract_date: new Date().toLocaleDateString('ro-RO'),
  contract_start_date: '',
  deviz_referinta: '',
  deviz_data: '',
  data_evaluare: new Date().toLocaleDateString('ro-RO'),
  // Date Beneficiar/Contractor
  beneficiar_cui: '',
  beneficiar_reg_com: '',
  beneficiar_name: '',
  beneficiar_address: '',
  beneficiar_reprezentant: '',
  beneficiar_functie: '',
  beneficiar_iban: '',
  beneficiar_banca: '',
  // Date Executant
  executant_name: '',
  executant_cui: '',
  executant_reg_com: '',
  executant_address: '',
  executant_reprezentant: '',
  executant_functie: 'Administrator',
  executant_iban: '',
  executant_banca: '',
  executant_telefon: '',
  // Obiect Contract
  obiect_lucrare: '',
  locatie_lucrare: '',
  // Preț și Plată
  pret_total: '',
  avans: '',
  rest_plata: '',
  // Garanție
  termen_executie: '30',
  garantie_luni: '60',
  // Observații
  observatii: ''
};

export default function ContractGeneratorDialog({ 
  open, 
  onOpenChange, 
  companyId, 
  companyData,
  editingContractId,
  editingContractData,
  prefillFromDeviz,
  onSave,
  onRefresh
}) {
  const [activeTab, setActiveTab] = useState('formular');
  const [contractData, setContractData] = useState(initialContractData);
  const [saving, setSaving] = useState(false);
  const [lookingUpCUI, setLookingUpCUI] = useState(false);

  // Precompletare date executant din companyData
  useEffect(() => {
    if (companyData && !editingContractId) {
      setContractData(prev => ({
        ...prev,
        executant_name: companyData.name || '',
        executant_cui: companyData.cui || '',
        executant_reg_com: companyData.reg_com || '',
        executant_address: `${companyData.address || ''}${companyData.city ? `, ${companyData.city}` : ''}${companyData.county ? `, ${companyData.county}` : ''}`,
        executant_reprezentant: companyData.admin_name || '',
        executant_functie: companyData.admin_function || 'Administrator',
        executant_iban: companyData.iban || '',
        executant_banca: companyData.bank_name || '',
        executant_telefon: companyData.phone || ''
      }));
    }
  }, [companyData, editingContractId]);

  // Încarcă date contract pentru editare
  useEffect(() => {
    if (editingContractId && editingContractData) {
      try {
        const parsed = typeof editingContractData === 'string' 
          ? JSON.parse(editingContractData) 
          : editingContractData;
        setContractData(parsed);
      } catch (e) {
        console.error('Error parsing contract data:', e);
      }
    } else if (!editingContractId && open) {
      // Reset la date noi + fetch next contract number (păstrăm câmpul editabil)
      const fetchAndSet = async () => {
        let nextNo = "";
        try {
          if (companyId) {
            const r = await axios.get(`${API}/contracts/next-number`, { params: { company_id: companyId } });
            nextNo = String(r.data?.next_number || "");
          }
        } catch { /* fallback to empty */ }
        // IMPORTANT: includem prefillFromDeviz aici pentru a evita race condition cu Effect B
        // (resetarea asincronă suprascria datele prefill din deviz).
        setContractData({
          ...initialContractData,
          contract_number: nextNo,
          executant_name: companyData?.name || '',
          executant_cui: companyData?.cui || '',
          executant_reg_com: companyData?.reg_com || '',
          executant_address: `${companyData?.address || ''}${companyData?.city ? `, ${companyData?.city}` : ''}${companyData?.county ? `, ${companyData?.county}` : ''}`,
          executant_reprezentant: companyData?.admin_name || '',
          executant_functie: companyData?.admin_function || 'Administrator',
          executant_iban: companyData?.iban || '',
          executant_banca: companyData?.bank_name || '',
          executant_telefon: companyData?.phone || '',
          // Suprascriem cu datele din deviz (au prioritate față de initial values, dar
          // păstrăm executant din companyData dacă deviz-ul nu le are completate)
          ...(prefillFromDeviz ? {
            ...prefillFromDeviz,
            executant_name: prefillFromDeviz.executant_name || companyData?.name || '',
            executant_cui: prefillFromDeviz.executant_cui || companyData?.cui || '',
            executant_address: prefillFromDeviz.executant_address || `${companyData?.address || ''}${companyData?.city ? `, ${companyData?.city}` : ''}${companyData?.county ? `, ${companyData?.county}` : ''}`,
            executant_telefon: prefillFromDeviz.executant_telefon || companyData?.phone || '',
          } : {}),
        });
      };
      fetchAndSet();
    }
  }, [editingContractId, editingContractData, companyData, companyId, open, prefillFromDeviz]);

  // Pre-completare din Deviz
  useEffect(() => {
    if (prefillFromDeviz && open && !editingContractId) {
      setContractData(prev => ({
        ...prev,
        ...prefillFromDeviz,
        // Păstrăm datele executantului din companyData dacă nu vin din deviz
        executant_name: prefillFromDeviz.executant_name || prev.executant_name,
        executant_cui: prefillFromDeviz.executant_cui || prev.executant_cui,
        executant_address: prefillFromDeviz.executant_address || prev.executant_address,
        executant_telefon: prefillFromDeviz.executant_telefon || prev.executant_telefon,
      }));
    }
  }, [prefillFromDeviz, open, editingContractId]);

  // Calculare automată rest plată
  useEffect(() => {
    const total = parseFloat(contractData.pret_total) || 0;
    const avans = parseFloat(contractData.avans) || 0;
    setContractData(prev => ({
      ...prev,
      rest_plata: (total - avans).toString()
    }));
  }, [contractData.pret_total, contractData.avans]);

  // Lookup CUI beneficiar
  const handleLookupCUI = async () => {
    if (!contractData.beneficiar_cui) {
      toast.error("Introduceți CUI-ul beneficiarului");
      return;
    }

    setLookingUpCUI(true);
    try {
      const res = await axios.get(`${API}/clients/lookup/${contractData.beneficiar_cui}?company_id=${companyId}`);
      const { data } = res.data;
      
      if (data) {
        setContractData(prev => ({
          ...prev,
          beneficiar_name: data.name || data.denumire || prev.beneficiar_name,
          beneficiar_reg_com: data.reg_com || data.numar_reg_com || prev.beneficiar_reg_com,
          beneficiar_address: data.address || data.adresa || prev.beneficiar_address
        }));
        toast.success("Date client preluate automat!");
      }
    } catch (error) {
      toast.error("Eroare la căutare CUI");
    } finally {
      setLookingUpCUI(false);
    }
  };

  // Update câmp
  const updateField = (field, value) => {
    setContractData(prev => ({ ...prev, [field]: value }));
  };

  // Salvare contract
  const handleSave = async (withDownload = false) => {
    if (!contractData.contract_number || !contractData.beneficiar_name || !contractData.obiect_lucrare) {
      toast.error("Completați câmpurile obligatorii: Nr. Contract, Beneficiar, Obiect");
      return;
    }

    // Verifică și deduce creditele doar la creare contract NOU
    // Editarea unui contract existent este gratuită
    if (!editingContractId) {
      const creditResult = await checkAndDeductCredits(
        companyId,
        'generate_contract',
        null,
        (warning) => toast.warning(warning, { duration: 8000 }),
        (block) => toast.error(block, { duration: 8000 })
      );
      
      if (!creditResult.canProceed) {
        return;
      }
      
      if (creditResult.usedBonus) {
        toast.warning("Ati folosit documentul bonus gratuit!", { duration: 5000 });
      }
    }

    setSaving(true);
    try {
      const htmlContent = generateContractHTML(contractData, companyData, { forPrint: true });
      
      if (editingContractId) {
        await axios.put(`${API}/documents/${editingContractId}`, {
          document_type: 'contract_servicii',
          title: `Contract Nr. ${contractData.contract_number} - ${contractData.beneficiar_name}`,
          html_content: htmlContent,
          contract_data: JSON.stringify(contractData),
          site_name: contractData.locatie_lucrare
        }, { params: { company_id: companyId } });
        toast.success("Contract actualizat!");
      } else {
        const createResp = await axios.post(`${API}/documents?company_id=${companyId}`, {
          title: `Contract Nr. ${contractData.contract_number} - ${contractData.beneficiar_name}`,
          document_type: 'contract_servicii',
          html_content: htmlContent,
          contract_data: JSON.stringify(contractData),
          site_name: contractData.locatie_lucrare
        });
        
        // Marchează devizul ca având contract generat
        if (contractData.deviz_id) {
          try {
            await axios.put(`${API}/devize/${contractData.deviz_id}?company_id=${companyId}`, {
              contract_generated: true
            });
          } catch (e) { console.error('Failed to mark deviz contract_generated:', e); }
        }
        
        const ap = createResp?.data?.auto_project;
        if (ap?.site_name) {
          toast.success("Contract salvat!", {
            description: `Proiect generat automat: "${ap.site_name}"${ap.phases_count ? ` cu ${ap.phases_count} etape` : ''}.`,
          });
        } else {
          toast.success("Contract salvat!");
        }
      }

      if (withDownload) {
        await handleDownload();
      }

      onRefresh && onRefresh();
      if (!withDownload) onOpenChange(false);
    } catch (error) {
      toast.error("Eroare la salvare");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  // Print preview
  const handlePrint = () => {
    const printWindow = safeOpenHTMLWindow(generateContractHTML(contractData, companyData, { forPrint: true }));
    if (printWindow) printWindow.print();
  };

  // Download PDF - server-side generation via weasyprint (identical to Print quality)
  const handleDownload = async () => {
    toast.info('Se generează PDF-ul...');
    
    try {
      const htmlContent = generateContractHTML(contractData, companyData, { forPrint: true });
      const filename = generateContractFilename(contractData, contractData.beneficiar_name);
      
      const response = await axios.post(`${API}/documents/generate-pdf`, {
        html_content: htmlContent,
        filename: filename
      }, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${filename}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF generat cu succes!');
    } catch (error) {
      console.error('Eroare la generarea PDF:', error);
      toast.error('Eroare la generarea PDF-ului');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl h-[90vh] p-0 flex flex-col">
        <DialogHeader className="p-4 pb-0">
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-600" />
            Generator Contract Prestări Servicii
            {editingContractId && <span className="text-sm text-amber-600 font-normal">(Editare)</span>}
            {prefillFromDeviz && !editingContractId && (
              <span className="text-sm text-indigo-600 font-normal">(din Deviz {prefillFromDeviz.deviz_referinta})</span>
            )}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="mx-4 grid w-auto grid-cols-2 max-w-md">
            <TabsTrigger value="formular" className="flex items-center gap-2">
              <FormInput className="w-4 h-4" />
              Formular
            </TabsTrigger>
            <TabsTrigger value="preview" className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Preview A4
            </TabsTrigger>
          </TabsList>

          {/* Tab Formular */}
          <TabsContent value="formular" className="flex-1 overflow-auto p-4 pt-2 m-0">
            <div className="space-y-6 max-w-4xl mx-auto">
              {/* Secțiunea 1: Identificare Contract */}
              <div className="bg-slate-50 p-4 rounded-lg">
                <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Hash className="w-4 h-4" />
                  Identificare Contract
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Nr. Contract *</Label>
                    <Input 
                      placeholder="ex: 92" 
                      value={contractData.contract_number}
                      onChange={e => updateField('contract_number', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Data Contract *</Label>
                    <Input 
                      type="date"
                      value={contractData.contract_date}
                      onChange={e => updateField('contract_date', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Data Începere</Label>
                    <Input 
                      type="date"
                      value={contractData.contract_start_date}
                      onChange={e => updateField('contract_start_date', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Deviz Referință</Label>
                    <Input 
                      placeholder="ex: 001"
                      value={contractData.deviz_referinta}
                      onChange={e => updateField('deviz_referinta', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Data Deviz</Label>
                    <Input 
                      type="date"
                      value={contractData.deviz_data}
                      onChange={e => updateField('deviz_data', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Data Evaluare</Label>
                    <Input 
                      type="date"
                      value={contractData.data_evaluare}
                      onChange={e => updateField('data_evaluare', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Secțiunea 2: Date Beneficiar */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-700 mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Date Beneficiar (Client)
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Label>CUI Beneficiar *</Label>
                      <Input 
                        placeholder="12345678"
                        value={contractData.beneficiar_cui}
                        onChange={e => updateField('beneficiar_cui', e.target.value)}
                      />
                    </div>
                    <div className="pt-6">
                      <Button variant="outline" size="icon" onClick={handleLookupCUI} disabled={lookingUpCUI}>
                        {lookingUpCUI ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label>Nr. Reg. Comerț</Label>
                    <Input 
                      placeholder="J40/1234/2020"
                      value={contractData.beneficiar_reg_com}
                      onChange={e => updateField('beneficiar_reg_com', e.target.value)}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Denumire Firmă *</Label>
                    <Input 
                      placeholder="S.C. Exemplu S.R.L."
                      value={contractData.beneficiar_name}
                      onChange={e => updateField('beneficiar_name', e.target.value)}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Adresă Sediu Social</Label>
                    <Input 
                      placeholder="Str. Exemplu, Nr. 1, Oraș, Județ"
                      value={contractData.beneficiar_address}
                      onChange={e => updateField('beneficiar_address', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Reprezentant Legal</Label>
                    <Input 
                      placeholder="Nume Prenume"
                      value={contractData.beneficiar_reprezentant}
                      onChange={e => updateField('beneficiar_reprezentant', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Funcție Reprezentant</Label>
                    <Input 
                      placeholder="Administrator"
                      value={contractData.beneficiar_functie}
                      onChange={e => updateField('beneficiar_functie', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>IBAN (opțional)</Label>
                    <Input 
                      placeholder="RO..."
                      value={contractData.beneficiar_iban}
                      onChange={e => updateField('beneficiar_iban', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Banca (opțional)</Label>
                    <Input 
                      placeholder="Nume Bancă"
                      value={contractData.beneficiar_banca}
                      onChange={e => updateField('beneficiar_banca', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Secțiunea 2.5: Date Executant (Read-only) */}
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h3 className="font-semibold text-purple-700 mb-3 flex items-center gap-2">
                  <Building className="w-4 h-4" />
                  Date Executant (Prestator) - Precompletate
                </h3>
                {companyData ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-purple-600 font-medium">Denumire:</span>
                        <p className="text-slate-700">{companyData.name || '-'}</p>
                      </div>
                      <div>
                        <span className="text-purple-600 font-medium">CUI:</span>
                        <p className="text-slate-700">{companyData.cui || '-'}</p>
                      </div>
                      <div>
                        <span className="text-purple-600 font-medium">Nr. Reg. Comerț:</span>
                        <p className="text-slate-700">{companyData.reg_com || '-'}</p>
                      </div>
                      <div>
                        <span className="text-purple-600 font-medium">Reprezentant:</span>
                        <p className="text-slate-700">{companyData.admin_name || '-'}</p>
                      </div>
                      <div className="col-span-2">
                        <span className="text-purple-600 font-medium">Adresă:</span>
                        <p className="text-slate-700">{companyData.address || '-'}{companyData.city ? `, ${companyData.city}` : ''}{companyData.county ? `, ${companyData.county}` : ''}</p>
                      </div>
                      <div>
                        <span className="text-purple-600 font-medium">IBAN:</span>
                        <p className="text-slate-700">{companyData.iban || '-'}</p>
                      </div>
                      <div>
                        <span className="text-purple-600 font-medium">Bancă:</span>
                        <p className="text-slate-700">{companyData.bank_name || '-'}</p>
                      </div>
                    </div>
                    <p className="text-xs text-purple-500 mt-2 italic">
                      * Aceste date sunt preluate din Setările Firmei. Pentru modificări, folosiți butonul "Setări Firmă" din header.
                    </p>
                  </div>
                ) : (
                  <p className="text-purple-600 text-sm">
                    Datele firmei nu sunt configurate. Completați din "Setări Firmă" pentru a genera contracte.
                  </p>
                )}
              </div>

              {/* Secțiunea 3: Obiect Contract */}
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Obiect Contract
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label>Descriere Lucrare *</Label>
                    <Textarea 
                      placeholder="ex: Hidroizolații cu membrană bituminoasă pentru terasă bloc..."
                      value={contractData.obiect_lucrare}
                      onChange={e => updateField('obiect_lucrare', e.target.value)}
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Locație Lucrare</Label>
                    <Input 
                      placeholder="ex: Str. Exemplu Nr. 10, Iași"
                      value={contractData.locatie_lucrare}
                      onChange={e => updateField('locatie_lucrare', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Secțiunea 4: Preț și Termene */}
              <div className="bg-amber-50 p-4 rounded-lg">
                <h3 className="font-semibold text-amber-700 mb-3 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Preț și Termene
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Preț Total (lei cu TVA) *</Label>
                    <Input 
                      type="number"
                      placeholder="12200"
                      value={contractData.pret_total}
                      onChange={e => updateField('pret_total', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Avans (lei cu TVA)</Label>
                    <Input 
                      type="number"
                      placeholder="6100"
                      value={contractData.avans}
                      onChange={e => updateField('avans', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Rest Plată (lei cu TVA)</Label>
                    <Input 
                      type="number"
                      placeholder="6100"
                      value={contractData.rest_plata}
                      readOnly
                      className="bg-slate-100"
                    />
                  </div>
                  <div>
                    <Label>Termen Execuție (zile)</Label>
                    <Input 
                      type="number"
                      placeholder="30"
                      value={contractData.termen_executie}
                      onChange={e => updateField('termen_executie', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Garanție (luni)</Label>
                    <Input 
                      type="number"
                      placeholder="60"
                      value={contractData.garantie_luni}
                      onChange={e => updateField('garantie_luni', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Secțiunea 5: Observații */}
              <div className="bg-slate-50 p-4 rounded-lg">
                <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Observații / Clauze Adiționale (opțional)
                </h3>
                <Textarea 
                  placeholder="Clauze speciale sau observații..."
                  value={contractData.observatii}
                  onChange={e => updateField('observatii', e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          </TabsContent>

          {/* Tab Preview */}
          <TabsContent value="preview" className="flex-1 overflow-hidden m-0">
            <ContractPreview 
              contractData={contractData}
              companyData={companyData}
              onPrint={handlePrint}
              onDownload={handleDownload}
            />
          </TabsContent>
        </Tabs>

        {/* Footer cu butoane */}
        <div className="p-4 border-t bg-slate-50 flex justify-between items-center">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Anulează
          </Button>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setActiveTab('preview')}
              className="text-blue-600"
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </Button>
            <Button 
              variant="outline"
              onClick={() => handleSave(true)}
              disabled={saving}
            >
              <Download className="w-4 h-4 mr-2" />
              Salvează + Descarcă
            </Button>
            <Button 
              onClick={() => handleSave(false)}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {editingContractId ? 'Actualizează' : 'Salvează'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
